import ApiService from '@/services/api'
import {
  saveProject,
  saveStyle,
  saveOption,
  saveDetail,
  buildFilterQuery
} from '@/store/utils'
import { styleMetaKeys } from '@/constants/meta'

/**
 * @param   {Store}        context
 * @param   {Store.commit} context.commit
 * @param   {object}       payload
 *
 * @returns {object}
 */
const createStyle = async ({ commit }, payload) => {
  const response = await ApiService.createStyle(
    payload.projectId,
    payload.boardId,
    payload.name,
    payload.layout,
    payload.index,
    payload.meta
  )
  const style = response.data

  commit('addStyle', style)

  return style
}

/**
 * @param {Store}        context
 * @param {Store.commit} context.commit
 * @param {object}       payload
 */
const deleteStyle = async ({ commit }, payload) => {
  await ApiService.deleteStyle(payload.styleId)

  commit('deleteStyle', payload.styleId)
}

/**
 * @param   {Store}         context
 * @param   {Store.commit}  context.commit
 * @param   {Store.state}   context.state
 * @param   {Store.getters} context.getters
 * @param   {object}        payload
 *
 * @returns {object}
 */
const fetchStyle = async ({ commit, state, getters }, payload) => {
  let response
  try {
    response = await ApiService.fetchStyle(payload.styleId)
  } catch (err) {
    console.log(err.status)
  }

  const style = response.data

  style.projectId = payload.projectId

  saveStyle({ commit, getters }, style)

  const options = style.options

  options.forEach(option => {
    const newOption = {
      ...option,
      style,
      projectId: style.projectId
    }
    saveOption({ commit, getters }, newOption)
  })

  const details = style.details

  details.forEach(detail => {
    saveDetail({ commit, getters }, detail)
  })

  return style
}

/**
 * @param   {Store}        context
 * @param   {Store.commit} context.commit
 * @param   {object}       payload
 *
 * @returns {object}
 */
const updateStyle = async ({ commit }, payload) => {
  const response = await ApiService.updateStyle(
    payload.styleId,
    payload.name,
    payload.notes,
    payload.layout,
    payload.index,
    payload.newBoardId,
    payload.meta,
    payload.attributes
  )

  const style = response.data

  style.projectId = payload.projectId

  commit('updateStyle', style)

  return style
}

/**
 * @param   {Store}         context
 * @param   {Store.commit}  context.commit
 * @param   {Store.getters} context.getters
 * @param   {object}        payload
 *
 * @returns {object}
 */
const updateStyleStoreOnly = ({ commit, getters }, payload) => {
  const styleFromStore = getters.getStyleById(payload.styleId)
  const style = {
    ...styleFromStore
  }

  if (payload.name) {
    style.name = payload.name
  }

  if (payload.notes) {
    style.notes = payload.notes
  }

  if (payload.index || payload.index === 0) {
    style.index = payload.index
  }

  if (payload.meta) {
    style.meta = payload.meta
  }

  if (payload.boardId) {
    style.board_id = payload.boardId
  }

  commit('updateStyle', style)

  return style
}

/**
 * @param   {Store}        context
 * @param   {Store.commit} context.commit
 * @param   {object}       payload
 *
 * @returns {object}
 */
const updateStyleName = async ({ commit }, payload) => {
  const response = await ApiService.updateStyleName(
    payload.projectId,
    payload.boardId,
    payload.styleId,
    payload.styleName
  )
  const style = response.data

  commit('updateStyle', style)

  return style
}

/**
 * @param   {Store}        context
 * @param   {Store.commit} context.commit
 * @param   {object}       payload
 *
 * @returns {object}
 */
const updateStyleLabel = async ({ commit }, payload) => {
  const response = await ApiService.updateStyleLabel(
    payload.styleId,
    payload.label
  )

  // To handle the axios.CancelToken on updateStyleLabel on api.js
  if (response && response.data) {
    const style = response.data

    style.projectId = payload.projectId

    commit('updateStyle', style)

    return style
  }
}

/**
 * @param   {Store}         context
 * @param   {Store.commit}  context.commit
 * @param   {Store.getters} context.getters
 * @param   {object}        payload
 *
 * @returns {object[]}
 */
const fetchCustomTags = async ({ commit, getters }, payload) => {
  const response = await ApiService.fetchCustomTags(
    payload.groupId,
    payload.seasonId,
    payload.assignedOnly
  )
  const tags = response.data

  commit('setCustomTags', tags)

  return tags
}

/**
 * @param   {Store}         context
 * @param   {Store.commit}  context.commit
 * @param   {Store.getters} context.getters
 * @param   {object}        payload
 *
 * @returns {object}
 */
const createStyleCustomTag = async ({ commit, getters }, payload) => {
  const payloadStyle = getters.getStyleById(payload.styleId)
  const style = JSON.parse(JSON.stringify(payloadStyle))

  const response = await ApiService.createStyleCustomTag(
    payload.styleId,
    payload.text
  )
  let tag = response.data

  if (tag && tag.id && tag.text) {
    commit('addCustomTag', tag)
  } else {
    tag = getters.getCustomTagByText(payload.text)
  }

  if (tag && tag.text) {
    style.custom_tags.push(tag)
  }

  commit('updateStyle', style)

  return style
}

/**
 * The function updates first the store so that
 * users can quickly delete tags with multiple backspace presses.
 *
 * @param   {Store}         context
 * @param   {Store.commit}  context.commit
 * @param   {Store.getters} context.getters
 * @param   {object}        payload
 *
 * @returns {object}
 */
const deleteStyleCustomTag = async ({ commit, getters }, payload) => {
  const style = getters.getStyleById(payload.styleId)
  const index = style.custom_tags.findIndex(tag => tag.id === payload.tagId)

  const styleNew = {
    ...style,
    custom_tags: [...style.custom_tags]
  }

  styleNew.custom_tags.splice(index, 1)

  commit('updateStyle', styleNew)

  await ApiService.deleteStyleCustomTag(payload.styleId, payload.tagId)

  return style
}

/**
 * @param   {Store}        context
 * @param   {Store.commit} context.commit
 *
 * @returns {object[]}
 */
const fetchStyleMetaFilters = async ({ commit }) => {
  const promises = Object.values(styleMetaKeys).map(key =>
    ApiService.fetchMetaFilters(key)
  )

  const result = await Promise.all(promises)

  const filters = {
    [styleMetaKeys.PRICE_ID]: {
      name: 'Pricing',
      values: result[0].data
    },
    [styleMetaKeys.PATTERN_ID]: {
      name: 'Solid, Print, Pattern',
      values: result[1].data
    }
  }

  commit('setStyleMetaFilters', filters)

  return filters
}

/**
 * @param {Store}        context
 * @param {Store.commit} context.commit
 */
const fetchStyleHubFilters = async ({ commit }) => {
  const result = await ApiService.fetchHubFilters()
  const filters = result.data.reduce((previous, { name, values }) => {
    return { ...previous, [name]: { name, values } }
  }, [])

  commit('setStyleHubFilters', filters)
}

/**
 * @param   {Store}         context
 * @param   {Store.commit}  context.commit
 * @param   {Store.getters} context.getters
 * @param   {object}        payload
 *
 * @returns {object}
 */
const createStyleMetaFilter = async ({ commit, getters }, payload) => {
  if (!payload.value) {
    return
  }

  const response = await ApiService.createStyleMetaFilter(
    payload.field,
    payload.value,
    payload.styleId
  )

  return response.data
}

/**
 * @param   {Store}         context
 * @param   {Store.commit}  context.commit
 * @param   {Store.getters} context.getters
 * @param   {object}        payload
 *
 * @returns {object[]}
 */
const fetchStylesPaginated = async ({ commit, getters }, payload) => {
  const limit = payload.limit || getters.getStylesPaginatedLimit
  const page = payload.page || getters.getStylesPaginatedPage
  const filterGroups = {
    ...getters.getStyleMetaFilters,
    ...getters.getOptionMetaFilters
  }
  const filterQuery = buildFilterQuery(payload.filters, filterGroups, false)

  const response = await ApiService.fetchStylesPaginated(
    payload.groupId,
    payload.seasonId,
    payload.projectId,
    page,
    limit,
    filterQuery,
    payload.searchQuery,
    payload.order
  )

  // error has already addressed by the APIService
  if (response.message || response.canceled) {
    return
  }

  // TODO make use of the next and previous that are in the data object
  const styles = response.data.results
  const options = []

  styles.forEach(style => {
    saveStyle({ commit, getters }, style)
    saveProject({ commit, getters }, style.project)

    style.options.forEach(optionFromServer => {
      const option = {
        project: style.project,
        ...optionFromServer
      }

      saveOption({ commit, getters }, option)
      options.push(option)
    })
  })

  if (response.data.next === null) {
    commit('setHasReachedStylesPaginatedEnd', true)
  }

  return options
}

/**
 * @param {object}       context
 * @param {Store.commit} context.commit
 */
const incrementStylesPaginatedPage = ({ commit }) => {
  commit('incrementStylesPaginatedPage')
}

/**
 * @param {object}       context
 * @param {Store.commit} context.commit
 */
const resetStylesPaginated = ({ commit }) => {
  commit('resetStylesPaginated')
}

/**
 * @type {object}
 */
const actions = {
  createStyle,
  deleteStyle,
  fetchStyle,
  updateStyle,
  updateStyleName,
  updateStyleLabel,
  updateStyleStoreOnly,
  fetchCustomTags,
  createStyleCustomTag,
  deleteStyleCustomTag,
  fetchStyleMetaFilters,
  fetchStyleHubFilters,
  createStyleMetaFilter,
  fetchStylesPaginated,
  incrementStylesPaginatedPage,
  resetStylesPaginated
}

export default actions
